import { Box, Flex } from "@radix-ui/themes";
import { SidebarFilters } from "./ui/sidebar-filters";
import { F1ScoreChart } from "./ui/f1-score-chart";
import { DashboardOrders } from "./ui/dashboard-orders";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getInitialFilters } from "./lib/helpers";
import { useUpdateEffect } from "react-use";
import { getInitialSorting } from "./lib/orders-helpers";
import { SortingState } from "@tanstack/react-table";
import { useAppDispatch } from "@/app/appStore";
import { insightsApi } from "@/shared/api";
import { flushSync } from "react-dom";

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState(() => getInitialFilters(searchParams));
  const [sorting, setSorting] = useState<SortingState>(() =>
    getInitialSorting(searchParams)
  );
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeSkus = async () => {
      const skuIds = searchParams.get("sku_ids")?.split(",");

      if (!skuIds?.length) {
        setIsInitialized(true);
        return;
      }

      const skuIdPromises = skuIds.map((skuId) =>
        dispatch(
          insightsApi.endpoints.variants.initiate({ sku_id: skuId })
        ).unwrap()
      );

      try {
        const skuResponses = await Promise.all(skuIdPromises);
        const selectedSkus = skuResponses.map((response) => ({
          value: response.results[0].id,
          label: response.results[0].display_name,
          image: response.results[0].image,
        }));

        flushSync(() => {
          setFilters((prevState) => ({ ...prevState, sku_ids: selectedSkus }));
        });

        setIsInitialized(true);
      } catch (err) {
        setIsInitialized(true);
      }
    };

    initializeSkus();
  }, []);

  useUpdateEffect(() => {
    const search = new URLSearchParams();

    if (filters.from_datetime) {
      search.set("from_datetime", filters.from_datetime.toISOString());
    }

    if (filters.to_datetime) {
      search.set("to_datetime", filters.to_datetime.toISOString());
    }

    if (filters.journeys_count) {
      search.set("journeys_count", filters.journeys_count);
    }

    if (
      filters.stage_journey_annotation &&
      filters.stage_journey_annotation !== "all"
    ) {
      search.set("stage_journey_annotation", filters.stage_journey_annotation);
    }

    if (
      filters.stage_transaction_annotation &&
      filters.stage_transaction_annotation !== "all"
    ) {
      search.set(
        "stage_transaction_annotation",
        filters.stage_transaction_annotation
      );
    }

    if (
      filters.stage_track_persistence_annotation &&
      filters.stage_track_persistence_annotation !== "all"
    ) {
      search.set(
        "stage_track_persistence_annotation",
        filters.stage_track_persistence_annotation
      );
    }

    if (filters.order_source && filters.order_source !== "all") {
      search.set("order_source", filters.order_source);
    }

    if (filters.annotator_id && filters.annotator_id !== "all") {
      search.set("annotator_id", filters.annotator_id);
    }

    if (filters.order_id) {
      search.set("order_id", filters.order_id);
    }

    if (filters.sku_ids && filters.sku_ids.length > 0) {
      search.set(
        "sku_ids",
        filters.sku_ids.map(({ value }) => value).join(",")
      );
    }

    if (filters.journey_count_enabled !== undefined) {
      search.set(
        "journey_count_enabled",
        String(filters.journey_count_enabled)
      );
    }

    setSearchParams(search);
    localStorage.setItem("dashboard-filters", JSON.stringify(filters));
  }, [filters]);

  return (
    <Flex>
      <Box className="w-[calc(100%-258px)]">
        {isInitialized && (
          <>
            <F1ScoreChart filters={filters} />
            <DashboardOrders
              filters={filters}
              sorting={sorting}
              onSortingChange={setSorting}
              onFiltersChange={setFilters}
            />
          </>
        )}
      </Box>
      <SidebarFilters
        isInitialized={isInitialized}
        filters={filters}
        onApplyFilters={setFilters}
      />
    </Flex>
  );
}
